// src/features/infoscreens/pages/InfoscreenDetailsPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Tv as TvIcon,
  Key as KeyIcon,
  MeetingRoom as MeetingRoomIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { INFOSCREENS_LIST_PATH } from '../paths';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useInfoscreens } from '../hooks/useInfoscreens';
import { useRooms } from '../../rooms/hooks/useRooms';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { usePermissions } from '../../auth/hooks/usePermissions';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';
import { CLASSIFICATION_COLORS } from '../../rooms/utils/classification_colors';
import PageHeader from '../../../components/common/PageHeader';

export default function InfoscreenDetailsPage() {
  const { t } = useTranslation(['infoscreens', 'common', 'rooms']);
  const navigate = useNavigate();
  const { infoscreenId } = useParams();
  const { showError, showSuccess } = useSnackbar();
  const permissions = usePermissions();
  const { navigateBack } = useNavigationStack();

  // State
  const [infoscreen, setInfoscreen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);

  // API hooks
  const {
    fetchInfoscreenById,
    assignRoom,
    unassignRoom,
    error,
    clearError,
    loading: infoscreenLoading,
  } = useInfoscreens();
  
  const { fetchDepartmentRooms, loading: roomsLoading } = useRooms();

  const loadInfoscreenData = useCallback(async () => {
    if (!infoscreenId) return;

    setLoading(true);
    try {
      const result = await fetchInfoscreenById(infoscreenId);
      if (result.success) {
        setInfoscreen(result.data);
      } else {
        showError(t('infoscreens:errors.load_failed'));
      }
    } catch (error) {
      showError(t('infoscreens:errors.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [infoscreenId, fetchInfoscreenById, showError, t]);

  // Get currently assigned room IDs
  const assignedRoomIds = useMemo(() => {
    return new Set((infoscreen?.rooms || []).map(r => r.room_id));
  }, [infoscreen?.rooms]);

  // Load available rooms
  const loadAvailableRooms = useCallback(async () => {
    const result = await fetchDepartmentRooms(false);
    if (result.success) {
      const unassignedRooms = (result.data.rooms || []).filter(
        room => !assignedRoomIds.has(room.room_id)
      );
      setAvailableRooms(unassignedRooms);
    }
  }, [fetchDepartmentRooms, assignedRoomIds]);

  // Load infoscreen data
  useEffect(() => {
    loadInfoscreenData();
  }, [loadInfoscreenData]);

  useEffect(() => {
    if (dialogOpen) {
      loadAvailableRooms();
    }
  }, [dialogOpen, loadAvailableRooms]);

  const handleBack = useCallback(() => {
    navigateBack(INFOSCREENS_LIST_PATH);
  }, [navigateBack]);

  const handleEdit = useCallback(() => {
    navigate(`/infoscreens/${infoscreenId}/edit`);
  }, [navigate, infoscreenId]);

  const handleRoomClick = useCallback((roomId) => {
    navigate(`/rooms/${roomId}`);
  }, [navigate]);

  const handleOpenDialog = useCallback(() => {
    setDialogOpen(true);
    setSelectedRooms([]);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedRooms([]);
  }, []);

  const handleToggleRoom = useCallback((roomId) => {
    setSelectedRooms(prev => {
      if (prev.includes(roomId)) {
        return prev.filter(id => id !== roomId);
      }
      return [...prev, roomId];
    });
  }, []);

  const handleAssignRooms = useCallback(async () => {
    if (selectedRooms.length === 0) return;

    try {
      for (const roomId of selectedRooms) {
        const result = await assignRoom(infoscreenId, roomId);
        if (!result.success) {
          showError(result.error || t('infoscreens:errors.assign_room_failed'));
          return;
        }
      }

      showSuccess(t('infoscreens:success.rooms_assigned', { count: selectedRooms.length }));
      handleCloseDialog();
      loadInfoscreenData();
    } catch (error) {
      showError(t('infoscreens:errors.assign_room_failed'));
    }
  }, [selectedRooms, infoscreenId, assignRoom, showSuccess, showError, handleCloseDialog, loadInfoscreenData, t]);

  const handleUnassignRoom = useCallback(async (roomId, event) => {
    event.stopPropagation();
    
    try {
      const result = await unassignRoom(infoscreenId, roomId);
      if (result.success) {
        showSuccess(t('infoscreens:success.room_unassigned'));
        loadInfoscreenData();
      } else {
        showError(result.error || t('infoscreens:errors.unassign_room_failed'));
      }
    } catch (error) {
      showError(t('infoscreens:errors.unassign_room_failed'));
    }
  }, [infoscreenId, unassignRoom, showSuccess, showError, loadInfoscreenData, t]);

  // Check permissions
  const canUpdate = permissions.hasScope('infoscreen:update');
  const canAssignRooms = canUpdate;

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!infoscreen) {
    return (
      <Box>
        <PageHeader
          onBack={handleBack}
          title={t('infoscreens:messages.infoscreen_not_found')}
        />
        <Alert severity="error">
          {t('infoscreens:messages.infoscreen_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={handleBack}
        title={infoscreen.name}
        subtitle={<>{t('common:labels.id')} <UUIDDisplay uuid={infoscreen.infoscreen_id} /></>}
        actions={<>
          {canUpdate && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              {t('infoscreens:actions.edit')}
            </Button>
          )}
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} flexWrap={"wrap"}>
        {/* Infoscreen Details */}
        <Box flex={{ xs: '1 1 100%', md: '1 1 50%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <TvIcon color="primary" />
                {t('infoscreens:form.infoscreen_information')}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('infoscreens:labels.name')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {infoscreen.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <KeyIcon fontSize="small" />
                    {t('infoscreens:labels.login_code')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" fontFamily="monospace">
                    {infoscreen.login_code}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('infoscreens:labels.created')}
                  </Typography>
                  <Typography variant="body2">
                    <LocalizedDateDistance date={infoscreen.created_at} />
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Rooms Card */}
        <Box flex={{ xs: '1 1 100%', md: '1 1 50%' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <MeetingRoomIcon color="primary" />
                  {t('infoscreens:assigned_rooms')} ({infoscreen.rooms?.length || 0})
                </Typography>
                {canAssignRooms && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                    disabled={infoscreenLoading}
                  >
                    {t('infoscreens:actions.assign_rooms')}
                  </Button>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />

              {!infoscreen.rooms || infoscreen.rooms.length === 0 ? (
                <Alert severity="info">
                  {t('infoscreens:messages.no_rooms_assigned')}
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {infoscreen.rooms.map((room) => (
                    <Box
                      key={room.room_id}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => handleRoomClick(room.room_id)}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {room.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {/* eslint-disable-next-line i18next/no-literal-string */}
                            {room.area_m2} m² • {t(`rooms:classifications.${room.classification}`)}
                          </Typography>
                        </Box>
                        {canAssignRooms && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleUnassignRoom(room.room_id, e)}
                            disabled={infoscreenLoading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Assign Rooms Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('infoscreens:actions.assign_rooms')}</DialogTitle>
        <DialogContent>
          {roomsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : availableRooms.length === 0 ? (
            <Alert severity="info">
              {t('infoscreens:messages.no_available_rooms')}
            </Alert>
          ) : (
            <List>
              {availableRooms.map((room) => (
                <ListItem key={room.room_id} disablePadding>
                  <ListItemButton
                    onClick={() => handleToggleRoom(room.room_id)}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedRooms.includes(room.room_id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={room.name}
                      secondary={
                        <Box display={"flex"} gap={2}>
                          <Typography>
                            {/* eslint-disable-next-line i18next/no-literal-string */}
                            {room.area_m2} m²
                          </Typography>
                          <Typography>
                            •
                          </Typography>
                          <Chip
                            label={t(`rooms:classifications.${room.classification}`)}
                            size="small"
                            color={CLASSIFICATION_COLORS[room.classification]}
                            variant="outlined"
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={infoscreenLoading}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignRooms}
            disabled={infoscreenLoading || selectedRooms.length === 0}
          >
            {infoscreenLoading ? <CircularProgress size={24} /> : t('infoscreens:actions.assign_selected', { count: selectedRooms.length })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}